<template>
  <div class="sidebar-container" v-loading="loading">
    <div class="module-switcher">
      <div class="module-grid">
        <div class="module-item" 
             v-for="mod in dynamicModules" 
             :key="mod.id"
             :class="{ active: currentModuleId === mod.id }" 
             @click="switchModule(mod)">
          <div class="icon-circle">
            <i :class="mod.icon || 'el-icon-menu'"></i>
          </div>
          <span class="module-name">{{ mod.display_name }}</span>
        </div>
      </div>
    </div>

    <el-menu
      :default-active="activeMenu"
      class="el-menu-vertical custom-menu-style"
      background-color="#ffffff"
      text-color="#606266"
      active-text-color="#1890ff"
      router>
      
      <template v-for="item in currentMenus">
        <el-submenu v-if="hasSubMenus(item)" :index="item.path || item.id.toString()" :key="item.id">
          <template slot="title">
            <i :class="item.icon" v-if="item.icon"></i>
            <span>{{ item.display_name }}</span>
          </template>
          <el-menu-item 
            v-for="child in item.children_tree.filter(c => c.type === 'menu')" 
            :index="child.path" 
            :key="child.id">
            {{ child.display_name }}
          </el-menu-item>
        </el-submenu>

        <el-menu-item v-else-if="item.type === 'menu'" :index="item.path" :key="item.id">
          <i :class="item.icon" v-if="item.icon"></i>
          <span slot="title">{{ item.display_name }}</span>
        </el-menu-item>
      </template>

    </el-menu>
  </div>
</template>

<script>
import { loadMenusOnce } from '@/utils/menuService'

export default {
  name: 'AppMenu',
  data() {
    return {
      loading: false,
      dynamicModules: [],
      currentModuleId: null
    }
  },
  created() {
    const publicPaths = ['/login', '/sso/fastadmin']
    if (publicPaths.includes(this.$route.path)) {
      return
    }

    this.loadMenus()
  },
  watch: {
    // 监听路由变化，实时同步上方宫格的高亮状态
    '$route.path'(newPath) {
      this.setActiveModuleByRoute(newPath)
    }
  },
  computed: {
    // 根据当前选中的宫格模块，计算出左侧的菜单树
    currentMenus() {
      const activeModule = this.dynamicModules.find(m => m.id === this.currentModuleId)
      if (activeModule && activeModule.children_tree) {
        return activeModule.children_tree.filter(item => item.type === 'menu')
      }
      return []
    },
    // 核心重构：企业级防掉线高亮算法
    activeMenu() {
      const route = this.$route
      const { meta, path } = route
      // 如果后端权限字典的 meta 里配置了 activeMenu (用于隐藏页面的回显)
      // 例如：您在“编辑试卷”页面的 meta 里指定了 activeMenu: '/exam/paper'，那么就算路由变了，依然高亮试卷管理
      if (meta && meta.activeMenu) {
        return meta.activeMenu
      }
      // 默认返回当前路径
      return path
    }
  },
  methods: {
    loadMenus() {
      this.loading = true
      loadMenusOnce().then(data => {
        this.dynamicModules = data
        this.setActiveModuleByRoute(this.$route.path)
      }).finally(() => {
        this.loading = false
      })
    },
    
    // 路由深度溯源，点亮对应的宫格
    setActiveModuleByRoute(targetPath) {
      if (!this.dynamicModules || this.dynamicModules.length === 0) return

      let targetModule = null
      const routeMeta = this.$route && this.$route.meta ? this.$route.meta : {}
      const lookupPath = routeMeta.activeMenu || targetPath

      for (let mod of this.dynamicModules) {
        if (this.isPathInModule(lookupPath, mod)) {
          targetModule = mod
          break
        }
      }

      if (targetModule) {
        this.switchModule(targetModule, false)
      } else {
        this.switchModule(this.dynamicModules[0], false)
      }
    },

    isPathInModule(targetPath, moduleNode) {
      if (!moduleNode.children_tree) return false
      
      let found = false
      const checkPath = (nodes) => {
        for (let node of nodes) {
          if (node.path === targetPath) {
            found = true
            return
          }
          if (node.children_tree && node.children_tree.length > 0) {
            checkPath(node.children_tree)
          }
        }
      }
      
      checkPath(moduleNode.children_tree)
      return found
    },

    hasSubMenus(item) {
      if (!item.children_tree || item.children_tree.length === 0) return false;
      return item.children_tree.some(child => child.type === 'menu');
    },

    switchModule(mod, autoPush = true) {
      this.currentModuleId = mod.id
      this.$root.$emit('module-changed', mod.display_name)

      if (autoPush && mod.children_tree && mod.children_tree.length > 0) {
        const firstMenu = mod.children_tree.find(m => m.type === 'menu')
        if (firstMenu && firstMenu.path && this.$route.path !== firstMenu.path) {
          if (!this.hasSubMenus(firstMenu)) {
             this.$router.push(firstMenu.path).catch(()=>{})
          } else {
             const firstSub = firstMenu.children_tree.find(m => m.type === 'menu')
             if (firstSub && firstSub.path) this.$router.push(firstSub.path).catch(()=>{})
          }
        }
      }
    }
  }
}
</script>

<style scoped>
.sidebar-container { height: 100%; display: flex; flex-direction: column; }
.module-switcher { padding: 15px 5px 5px 5px; border-bottom: 1px solid #f0f2f5; flex-shrink: 0; }
.module-grid { display: flex; flex-wrap: wrap; }
.module-item { width: 33.33%; display: flex; flex-direction: column; align-items: center; margin-bottom: 15px; cursor: pointer; }
/* 九宫格默认样式 */
.icon-circle { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 16px; color: #909399; background-color: #f4f4f5; transition: all 0.2s; }
.module-name { font-size: 12px; color: #909399; margin-top: 6px; transform: scale(0.92); transition: all 0.2s; }
/* 九宫格鼠标悬浮和选中高亮样式 */
.module-item:hover .icon-circle, .module-item.active .icon-circle { color: #1890ff; background-color: #e6f7ff; }
.module-item:hover .module-name, .module-item.active .module-name { font-weight: bold; color: #1890ff; }

.el-menu-vertical { flex: 1; border-right: none; overflow-y: auto; }
.el-menu-vertical::-webkit-scrollbar { display: none; }
/* 优化 Element UI 左侧菜单选中高亮颜色，更贴近钉钉风格 */
.custom-menu-style .el-menu-item.is-active {
  background-color: #e6f7ff !important;
  border-right: 3px solid #1890ff;
  font-weight: bold;
}
</style>
